import { applyNodeChanges, type NodeChange } from '@xyflow/react';
import { produce } from 'immer';
import { partition } from 'lodash';
import { create } from 'zustand';

import { useCommandHistory } from './hooks/commands/hooks/useCommandHistory';
import { type GroupMutations, type NodeUpdate } from './hooks/sync/useCanvasSync';
import { type ZoomToFitParams } from './hooks/useZoomToFit';
import { type Orientation } from './nodes/Image/transforms/rotateFlip/orientationMaps';
import { TRANSFORM_STORE_CONFIGS } from './nodes/Image/transforms/storeConfigs';
import {
  type ActiveTool,
  type AppNode,
  type BoundingBox,
  type CanvasMode,
  type GetEditUploadUrlFn,
  type SaveCompositeParams,
} from './types';
import { type SnapLine } from './utils/nodeSnapping';
import { getResizeSnapTarget } from './utils/resizeSnapTarget';

export type ClientSidePreview = {
  nodeId: string;
  blobUrl: string;
  width: number;
  height: number;
};

type EditCanvasState = {
  signature: 'aircanvas';
  version: 1;
  activeTool: ActiveTool;
  aspectRatio: number | null;
  boundingBox: BoundingBox<'normalized'> | null;
  canDownload: boolean;
  canvasMode: CanvasMode;
  clientSidePreview: ClientSidePreview | null;
  isFullscreen: boolean;
  nodes: AppNode[];
  readOnly: boolean;
  showChatInReadOnly: boolean;
  renamingNodeId: string | null;
  rotateFlipPreview: Orientation | null;
  canvasId: string | null;
  showGuideGrid: boolean;
  drillDownGroupId: string | null;
  /** Snap-guide lines to render during node resize. Empty when no resize is active. */
  resizeSnapLines: SnapLine[];
  flushSync?: () => Promise<void>;
  getEditUploadUrl?: GetEditUploadUrlFn;
  groupMutations?: GroupMutations;
  onSaveToWorkspace?: (nodes: AppNode[]) => void;
  onSaveCompositeToWorkspace?: (params: SaveCompositeParams) => void;
  createNodes?: (nodes: AppNode[]) => void;
  updateNodes?: (updates: NodeUpdate[]) => void;
  deleteNodes?: (nodes: AppNode[]) => void;
  zoomToFit?: (params?: ZoomToFitParams) => void;
  selectionClickCount: number;
};

type CallbackKeys =
  | 'createNodes'
  | 'deleteNodes'
  | 'flushSync'
  | 'getEditUploadUrl'
  | 'groupMutations'
  | 'updateNodes'
  | 'onSaveToWorkspace'
  | 'onSaveCompositeToWorkspace';

type EditCanvasActions = {
  addNodes: (nodes: AppNode[], options?: { removeNodeIds?: string[]; unselectExistingNodes?: boolean }) => void;
  clearBoundingBox: () => void;
  moveNodes: (updates: Array<{ id: string; position: { x: number; y: number } }>) => void;
  onNodesChange: (changes: NodeChange<AppNode>[]) => void;
  removeNodes: (nodeIds: string[]) => void;
  resizeNodes: (updates: Array<{ id: string; width: number; height: number }>) => void;
  setActiveTool: (tool: ActiveTool) => void;
  setAspectRatio: (aspectRatio: number | null) => void;
  setBoundingBox: (boundingBox: BoundingBox<'normalized'>) => void;
  setCallbacks: (callbacks: Partial<Pick<EditCanvasState, CallbackKeys>>) => void;
  setCanvasId: (canvasId: string | null) => void;
  setCanvasMode: (canvasMode: CanvasMode, options?: { reselectNode?: boolean }) => void;
  setClientSidePreview: (preview: ClientSidePreview | null) => void;
  setNodes: (nodes: AppNode[], boundingBox?: BoundingBox<'normalized'> | null) => void;
  setFullscreen: (isFullscreen: boolean) => void;
  setCanDownload: (canDownload: boolean) => void;
  setReadOnly: (readOnly: boolean, canDownload?: boolean) => void;
  setShowChatInReadOnly: (showChatInReadOnly: boolean) => void;
  setRenamingNodeId: (nodeId: string | null) => void;
  setResizeSnapLines: (lines: SnapLine[]) => void;
  setRotateFlipPreview: (preview: Orientation | null) => void;
  updateNodeContent: (
    id: string,
    update: { data: NodeUpdate['data']; position: { x: number; y: number }; width: number; height: number },
  ) => void;
  updateNodeData: (nodeId: string, dataUpdates: Record<string, unknown>) => void;
  updateNodeRemoteId: (localId: string, remoteId: string) => void;
  updateZIndexes: (updates: Array<{ id: string; zIndex: number }>) => void;
  setGroupId: (nodeIds: string[], groupId: string | undefined) => void;
  setDrillDownGroupId: (groupId: string | null) => void;
  setZoomToFit: (fn: ((params?: ZoomToFitParams) => void) | undefined) => void;
  increaseSelectionClick: () => void;
};

const INITIAL_STATE: EditCanvasState = {
  activeTool: 'select',
  aspectRatio: null,
  boundingBox: null,
  canDownload: true,
  canvasId: null,
  canvasMode: { mode: 'default' },
  clientSidePreview: null,
  drillDownGroupId: null,
  isFullscreen: false,
  nodes: [],
  readOnly: false,
  renamingNodeId: null,
  resizeSnapLines: [],
  rotateFlipPreview: null,
  selectionClickCount: 0,
  showChatInReadOnly: false,
  showGuideGrid: false,
  signature: 'aircanvas',
  version: 1,
};

export const useEditCanvasStore = create<EditCanvasState & EditCanvasActions>((set, get) => ({
  ...INITIAL_STATE,

  addNodes: (newNodes, options) => {
    if (get().readOnly) {
      return;
    }

    const { removeNodeIds, unselectExistingNodes } = options ?? {};
    const maxZIndex = get().nodes.reduce((max, n) => Math.max(max, n.zIndex ?? 0), -1);
    const nodesWithZIndex = newNodes.map((node, i) => ({
      ...node,
      zIndex: node.zIndex ?? maxZIndex + 1 + i,
    }));
    const removeSet = removeNodeIds ? new Set(removeNodeIds) : null;

    set(
      produce((state: EditCanvasState) => {
        if (removeSet) {
          state.nodes = state.nodes.filter((n: AppNode) => !removeSet.has(n.id));
        }
        if (unselectExistingNodes) {
          for (const n of state.nodes) {
            if (n.selected) {
              n.selected = false;
            }
          }
        }
        state.nodes.push(...nodesWithZIndex);
      }),
    );

    get().createNodes?.(nodesWithZIndex);
  },

  clearBoundingBox: () => set({ boundingBox: null }),

  increaseSelectionClick: () => set((state) => ({ selectionClickCount: state.selectionClickCount + 1 })),

  moveNodes: (updates) => {
    set(
      produce((state: EditCanvasState) => {
        for (const update of updates) {
          const node = state.nodes.find((n: AppNode) => n.id === update.id);

          if (node) {
            node.position = update.position;
          }
        }
      }),
    );
    get().updateNodes?.(updates);
  },

  onNodesChange: (changes) =>
    set((state) => {
      // Substitute snap-corrected dimensions/position for the actively resizing
      // node. `<NodeResizer>` calls our resize-snap hook before dispatching its
      // change here, so this is where the snapped values land in the store.
      const interceptedChanges = changes.map((change) => {
        if (change.type === 'dimensions') {
          const target = getResizeSnapTarget(change.id);

          if (target && change.dimensions) {
            return { ...change, dimensions: { height: target.height, width: target.width } };
          }
        } else if (change.type === 'position') {
          const target = getResizeSnapTarget(change.id);

          if (target && change.position) {
            return { ...change, position: { x: target.x, y: target.y } };
          }
        }

        return change;
      });

      const nodes = applyNodeChanges(interceptedChanges, state.nodes);
      let extraState: Partial<EditCanvasState> = {};

      // Sync expanding bounding box node → boundingBox state
      const { canvasMode } = state;

      if (canvasMode.mode === 'extendBackground') {
        const expandingBB = nodes.find((n) => n.type === 'expandingBoundingBox');
        const imageNode = nodes.find((n) => n.id === canvasMode.nodeId);

        if (expandingBB && imageNode) {
          const imageW = typeof imageNode.style?.width === 'number' ? imageNode.style.width : 0;
          const imageH = typeof imageNode.style?.height === 'number' ? imageNode.style.height : 0;
          const bbW = expandingBB.width ?? (typeof expandingBB.style?.width === 'number' ? expandingBB.style.width : 0);
          const bbH =
            expandingBB.height ?? (typeof expandingBB.style?.height === 'number' ? expandingBB.style.height : 0);

          if (imageW > 0 && imageH > 0) {
            extraState = {
              boundingBox: {
                __unit: 'normalized',
                height: bbH / imageH,
                width: bbW / imageW,
                x: (expandingBB.position.x - imageNode.position.x) / imageW,
                y: (expandingBB.position.y - imageNode.position.y) / imageH,
              },
            };
          }
        }
      }

      // Auto-exit drill-down when clicking outside the group (no group member remains selected).
      // Don't re-select members here — the always-on overlay handles selection on the next click.
      if (state.drillDownGroupId) {
        const anyGroupMemberSelected = nodes.some((n) => n.groupId === state.drillDownGroupId && n.selected);

        if (!anyGroupMemberSelected) {
          extraState = { ...extraState, drillDownGroupId: null };
        }
      }

      return { ...extraState, nodes };
    }),

  removeNodes: (nodeIds) => {
    const idsToRemove = new Set(nodeIds);
    const [removedNodes, remainingNodes] = partition(get().nodes, (n) => idsToRemove.has(n.id));
    set({ nodes: remainingNodes });
    get().deleteNodes?.(removedNodes);
  },

  resizeNodes: (updates) => {
    set(
      produce((state: EditCanvasState) => {
        for (const update of updates) {
          const node = state.nodes.find((n: AppNode) => n.id === update.id);

          if (node) {
            node.style = { ...node.style, height: update.height, width: update.width };
            // Clear ReactFlow's measured/direct dimensions so the updated style
            // values take effect on the next render. Without this, undoing a
            // resize leaves the node visually at its post-resize size because
            // measured/width/height — set by <NodeResizer> via applyNodeChanges
            // — outrank style. Mirrors the pattern in updateNodeContent below.
            node.measured = undefined;
            node.width = undefined;
            node.height = undefined;
          }
        }
      }),
    );
    get().updateNodes?.(updates);
  },

  setActiveTool: (tool) =>
    set(
      produce((state: EditCanvasState) => {
        state.activeTool = tool;
        if (tool === 'hand') {
          for (const node of state.nodes) {
            node.selected = false;
          }
        }
      }),
    ),

  setAspectRatio: (aspectRatio) => set({ aspectRatio }),

  setBoundingBox: (boundingBox) => set({ boundingBox }),

  setCallbacks: (callbacks) => set(callbacks),

  setCanDownload: (canDownload) => set({ canDownload }),

  setCanvasId: (canvasId) => {
    if (canvasId !== useEditCanvasStore.getState().canvasId) {
      // Reset all transient state when switching canvases to avoid stale
      // mode, selection, or preview state leaking across canvases.
      set({
        activeTool: 'select',
        aspectRatio: null,
        boundingBox: null,
        canvasId,
        canvasMode: { mode: 'default' },
        clientSidePreview: null,
        drillDownGroupId: null,
        renamingNodeId: null,
        rotateFlipPreview: null,
        showGuideGrid: false,
      });
    }
  },

  setCanvasMode: (canvasMode, options) => {
    if (get().readOnly && canvasMode.mode !== 'default') {
      return;
    }
    const reselectNode = options?.reselectNode ?? true;
    const prevMode = useEditCanvasStore.getState().canvasMode;
    const prevConfig = prevMode.mode !== 'default' ? TRANSFORM_STORE_CONFIGS[prevMode.mode] : undefined;
    const nextConfig = canvasMode.mode !== 'default' ? TRANSFORM_STORE_CONFIGS[canvasMode.mode] : undefined;

    // Note: transitioning directly between two isolated-history modes is not supported
    const entering = nextConfig?.isolateCommandHistory;
    const leaving = prevConfig?.isolateCommandHistory;

    if (entering) {
      useCommandHistory.getState().pushStack();
    }
    if (leaving) {
      useCommandHistory.getState().popStack();
    }

    // Reset to select tool when entering a transform mode
    if (canvasMode.mode !== 'default') {
      set({ activeTool: 'select' });
    }

    set((state) => {
      // Remove existing expanding bounding box nodes and reset per-node overrides
      let nodes: AppNode[] = state.nodes
        .filter((obj) => obj.type !== 'expandingBoundingBox')
        .map(
          (obj): AppNode => ({
            ...obj,
            draggable: undefined,
            selected:
              canvasMode.mode === 'default'
                ? reselectNode && 'nodeId' in prevMode && obj.id === prevMode.nodeId
                : false,
          }),
        );

      // Add expanding bounding box when entering extendBackground mode
      if (canvasMode.mode === 'extendBackground') {
        const imageNode = nodes.find((o) => o.id === canvasMode.nodeId);

        if (imageNode) {
          const imageW = typeof imageNode.style?.width === 'number' ? imageNode.style.width : 0;
          const imageH = typeof imageNode.style?.height === 'number' ? imageNode.style.height : 0;

          // Lock the target image in place during extend background
          nodes = nodes.map((obj): AppNode => (obj.id === canvasMode.nodeId ? { ...obj, draggable: false } : obj));

          const expandingBoundingBox: AppNode = {
            data: { imageNodeId: canvasMode.nodeId },
            draggable: false,
            id: 'expanding-bounding-box',
            position: { x: imageNode.position.x, y: imageNode.position.y },
            style: { height: imageH, width: imageW },
            type: 'expandingBoundingBox',
          };

          nodes = [...nodes, expandingBoundingBox];
        }
      }

      return {
        aspectRatio: null,
        boundingBox: nextConfig?.usesBoundingBox ? (nextConfig.usesBoundingBox.initialBox ?? null) : null,
        canvasMode,
        nodes,
        rotateFlipPreview: null,
        showGuideGrid: !!nextConfig?.usesBoundingBox && !!nextConfig.usesBoundingBox.showGuideGrid,
      };
    });
  },

  setClientSidePreview: (preview) => set({ clientSidePreview: preview }),

  setDrillDownGroupId: (drillDownGroupId) => set({ drillDownGroupId }),

  setFullscreen: (isFullscreen) => set({ isFullscreen }),

  setGroupId: (nodeIds, groupId) => {
    const idSet = new Set(nodeIds);

    set(
      produce((state: EditCanvasState) => {
        for (const node of state.nodes) {
          if (idSet.has(node.id)) {
            node.groupId = groupId;
          }
        }
      }),
    );
  },

  setNodes: (nodes, boundingBox) => set(boundingBox !== undefined ? { boundingBox, nodes } : { nodes }),

  setReadOnly: (readOnly, canDownload) => {
    set({ readOnly });
    get().setActiveTool(readOnly && !canDownload ? 'hand' : 'select');
  },

  setRenamingNodeId: (nodeId) => set({ renamingNodeId: nodeId }),

  setResizeSnapLines: (lines) => set({ resizeSnapLines: lines }),

  setRotateFlipPreview: (preview) => set({ rotateFlipPreview: preview }),

  setShowChatInReadOnly: (showChatInReadOnly) => set({ showChatInReadOnly }),

  setZoomToFit: (fn) => set({ zoomToFit: fn }),

  updateNodeContent: (id, update) => {
    set((state) => ({
      nodes: state.nodes.map((node) => {
        if (node.id !== id) {
          return node;
        }

        return produce(node, (draft) => {
          // Replace data entirely (not merge) so stale keys like isProcessing are dropped
          for (const key of Object.keys(draft.data)) {
            if (!(key in Object(update.data))) {
              Reflect.deleteProperty(draft.data, key);
            }
          }

          Object.assign(draft.data, update.data);
          draft.position = update.position;
          draft.style = { ...draft.style, height: update.height, width: update.width };
          // Clear ReactFlow's measured/direct dimensions so it uses the updated style values
          draft.measured = undefined;
          draft.width = undefined;
          draft.height = undefined;
        });
      }),
    }));
    get().updateNodes?.([
      {
        data: update.data,
        height: update.height,
        id,
        position: update.position,
        width: update.width,
      },
    ]);
  },

  updateNodeData: (nodeId, dataUpdates) =>
    set((state) => ({
      nodes: state.nodes.map((obj) => {
        if (obj.id !== nodeId) {
          return obj;
        }

        return produce(obj, (draft) => {
          Object.assign(draft.data, dataUpdates);
        });
      }),
    })),

  updateNodeRemoteId: (localId, remoteId) =>
    set(
      produce((state: EditCanvasState) => {
        const node = state.nodes.find((n: AppNode) => n.id === localId);

        if (node) {
          node.remoteId = remoteId;
        }
      }),
    ),

  updateZIndexes: (updates) => {
    const updateMap = new Map(updates.map((u) => [u.id, u.zIndex]));
    set(
      produce((state: EditCanvasState) => {
        for (const node of state.nodes) {
          const newZIndex = updateMap.get(node.id);

          if (newZIndex !== undefined) {
            node.zIndex = newZIndex;
          }
        }
      }),
    );
    get().updateNodes?.(updates.map((u) => ({ id: u.id, zIndex: u.zIndex })));
  },
}));
